interface KaraokePricingProps {
  lang: 'en' | 'ja';
}

export default function KaraokePricing({ lang }: KaraokePricingProps) {
  const content = {
    en: {
      badge: 'Pricing',
      title: 'Simple & Transparent Pricing',
      subtitle: 'No hidden fees. Just fun.',
      basePrice: {
        duration: '3 Hours',
        price: '¥18,000',
        people: '1-2 people',
        features: [
          'Karaoke room pre-booking',
          'Reception & check-in support',
          'Machine operation guidance',
          'Song recommendations',
          'Join & sing along',
          'Q&A support'
        ]
      },
      additional: {
        title: 'Additional Guest',
        price: '+¥5,000',
        subtitle: 'per person',
        note: 'Maximum 4 people'
      },
      cta: 'Book Now',
      note: 'Room fee and food/drinks are paid directly to the venue.'
    },
    ja: {
      badge: '料金',
      title: 'シンプルで透明な料金',
      subtitle: '隠れた費用はありません。ただ楽しむだけ。',
      basePrice: {
        duration: '3時間',
        price: '¥18,000',
        people: '1〜2名',
        features: [
          'カラオケルーム事前予約',
          '受付・チェックインサポート',
          '機械操作ガイダンス',
          '曲のおすすめ',
          '一緒に歌って盛り上がる',
          'Q&Aサポート'
        ]
      },
      additional: {
        title: '追加ゲスト',
        price: '+¥5,000',
        subtitle: '1名あたり',
        note: '最大4名まで'
      },
      cta: '今すぐ予約',
      note: '部屋代および飲食代は店舗へ直接お支払いください。'
    }
  };

  const t = content[lang];

  const handleWhatsAppClick = () => {
    const message = lang === 'en' 
      ? 'Hi! I\'d like to book Tokyo Karaoke Night experience.'
      : 'こんにちは！Tokyo Karaoke Nightの予約をしたいです。';
    window.open(`https://wa.me/818041800418?text=${encodeURIComponent(message)}`, '_blank');
  };

  return (
    <section className="py-24 bg-gradient-to-b from-purple-50 to-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-pink-100 rounded-full mb-6">
            <i className="ri-price-tag-3-line text-pink-600"></i>
            <span className="text-pink-700 text-sm font-semibold tracking-wide uppercase">{t.badge}</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">
            {t.title}
          </h2>
          <p className="text-xl text-gray-600">
            {t.subtitle}
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid lg:grid-cols-2 gap-8 max-w-5xl mx-auto mb-12">
          {/* Base Price Card */}
          <div className="relative bg-gradient-to-br from-pink-500 to-purple-600 rounded-3xl p-8 shadow-2xl text-white overflow-hidden">
            {/* Decorative Elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
            
            <div className="relative">
              <div className="mb-6">
                <div className="text-sm font-semibold text-pink-200 mb-2">{t.basePrice.duration}</div>
                <div className="text-5xl font-black mb-2">{t.basePrice.price}</div>
                <div className="text-lg text-pink-100">{t.basePrice.people}</div>
              </div>

              <div className="space-y-3 mb-8">
                {t.basePrice.features.map((feature, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-pink-200 text-xl flex-shrink-0 mt-0.5"></i>
                    <span className="text-white/90">{feature}</span>
                  </div>
                ))}
              </div>

              <button
                onClick={handleWhatsAppClick}
                className="w-full bg-white text-purple-600 font-bold py-4 rounded-xl hover:bg-pink-50 transition-colors duration-300 flex items-center justify-center gap-2 whitespace-nowrap"
              >
                <i className="ri-whatsapp-line text-xl"></i>
                <span>{t.cta}</span>
              </button>
            </div>
          </div>

          {/* Additional Guest Card */}
          <div className="bg-white rounded-3xl p-8 shadow-xl border-2 border-purple-200 flex flex-col justify-center">
            <div className="text-center mb-6">
              <i className="ri-user-add-line text-5xl text-purple-600 mb-4"></i>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                {t.additional.title}
              </h3>
              <div className="text-4xl font-black text-purple-600 mb-2">
                {t.additional.price}
              </div>
              <div className="text-gray-600 mb-4">{t.additional.subtitle}</div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-100 rounded-full">
                <i className="ri-information-line text-purple-600"></i>
                <span className="text-sm font-semibold text-purple-700">{t.additional.note}</span>
              </div>
            </div>

            {/* Example Calculation */}
            <div className="bg-purple-50 rounded-xl p-6 border border-purple-200">
              <div className="text-sm font-semibold text-purple-700 mb-3">
                {lang === 'en' ? 'Example:' : '例：'}
              </div>
              <div className="space-y-2 text-gray-700">
                <div className="flex justify-between">
                  <span>{lang === 'en' ? '2 people' : '2名'}</span>
                  <span className="font-semibold">¥18,000</span>
                </div>
                <div className="flex justify-between">
                  <span>{lang === 'en' ? '3 people' : '3名'}</span>
                  <span className="font-semibold">¥23,000</span>
                </div>
                <div className="flex justify-between">
                  <span>{lang === 'en' ? '4 people' : '4名'}</span>
                  <span className="font-semibold">¥28,000</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Important Note */}
        <div className="max-w-3xl mx-auto bg-amber-50 border-l-4 border-amber-500 rounded-lg p-6">
          <div className="flex items-start gap-4">
            <i className="ri-information-line text-2xl text-amber-600 flex-shrink-0"></i>
            <p className="text-gray-700 leading-relaxed">
              <strong className="text-amber-900">{lang === 'en' ? 'Important:' : '重要：'}</strong> {t.note}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}